'use client'

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import type { SchoolGrade } from '@helper/utils/domain'
import { tryCatch } from '@helper/utils/core'
import { childAvatarList } from '@helper/utils/domain'

import { useAuthClient, useSharedTRPC } from '../../context'
import { useStudentMutations } from '../../tables'

export interface EnrollFailure {
  name: string
  message: string
}

export interface EnrollResult {
  succeeded: string[]
  failed: EnrollFailure[]
}

export interface EnrollOptions {
  /** Class (rombel) to drop every child into, or `null` to leave them unassigned. */
  teamId?: string | null
  /** Grade level, or `null` to leave it unset. */
  schoolClass?: SchoolGrade | null
  /** Avatar for the account. Omit to draw a random one per child, as bulk enrolment does. */
  image?: string
  /** Written after the account exists — `signUpChild` does not carry either field. */
  nickname?: string | null
  birthYear?: number | null
}

const randomAvatar = () => childAvatarList[Math.floor(Math.random() * childAvatarList.length)]?.src

/**
 * Creates child accounts one at a time and reports per-name outcomes.
 *
 * Sequential on purpose: `signUpChild` generates a PIN by drawing and re-drawing until the value is
 * free, so parallel sign-ups race each other on that check. A slower loop beats two children who
 * cannot both log in.
 *
 * Nickname and birth year are a second write. `signUpChild` accepts only name, image, team, and
 * grade, so the dialog creates the account first and fills the rest in through `editChild` — the
 * same procedure the account-settings sheet uses. A failure there leaves a usable account, so it
 * is reported as a warning rather than failing the enrolment.
 */
export function useEnrollChildren() {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const { data: activeOrganization } = authClient.useActiveOrganization()
  const { invalidate } = useStudentMutations()
  const editChild = useMutation(trpc.myOrganization.editChild.mutationOptions())

  const [progress, setProgress] = useState({ total: 0, completed: 0 })

  const enroll = async (names: string[], options: EnrollOptions = {}): Promise<EnrollResult> => {
    const { teamId = null, schoolClass = null, image, nickname = null, birthYear = null } = options

    if (!activeOrganization) {
      return { succeeded: [], failed: names.map((name) => ({ name, message: 'Organisasi tidak ditemukan' })) }
    }

    const succeeded: string[] = []
    const failed: EnrollFailure[] = []

    setProgress({ total: names.length, completed: 0 })

    for (const name of names) {
      const [createdId, error] = await tryCatch(async () => {
        const response = await authClient.signUpChild({
          name,
          organizationId: activeOrganization.id,
          image: image ?? randomAvatar(),
          teamId: teamId ?? undefined,
          schoolClass: schoolClass ?? undefined,
        })

        if (response.error) {
          throw new Error(response.error.message ?? 'Gagal mendaftarkan akun')
        }

        const data = response.data as { user?: { id?: string } } | null
        return data?.user?.id ?? null
      })

      if (error) {
        failed.push({ name, message: error.message })
      } else {
        succeeded.push(name)

        const hasDetails = (nickname !== null && nickname !== '') || birthYear !== null
        if (createdId && hasDetails) {
          await tryCatch(() =>
            editChild.mutateAsync({
              userId: createdId,
              data: {
                ...(nickname !== null && nickname !== '' ? { nickname } : {}),
                ...(birthYear !== null ? { birthYear } : {}),
              },
            }),
          )
        }
      }

      setProgress((prev) => ({ ...prev, completed: prev.completed + 1 }))
    }

    // Invalidate whenever anything landed — a partial batch still changed the roster, and the
    // dialog stays open on failures so the table behind it must already be current.
    if (succeeded.length > 0) {
      await invalidate()
    }

    setProgress({ total: 0, completed: 0 })

    return { succeeded, failed }
  }

  return { enroll, progress, isReady: !!activeOrganization }
}
